<script setup lang="ts">
import { computed } from "vue";

import HierarchyStructure from "./HierarchyStructure.vue";
import type { Branch } from "./useExcludedBranch";
import type { useHierarchyTree } from "./useHierarchyTree";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;

const { tree, node, branch, close } = defineProps<{
	tree: ReturnType<typeof useHierarchyTree>;
	node: QuickFilterNodeDTO;
	branch: Branch;

	close: () => void;
}>();

const expanded = computed(() => tree.isExpanded(branch));

const isCurrent = computed(
	() => tree.scopeBranch.value.join() === branch.join()
);

const isPruned = computed(() => tree.isBranchPruned(branch));

const children = computed(() =>
	(node.children ?? [])
		.map((child, index) => ({ child, branch: [...branch, index] }))
		.filter((entry) => !entry.child.isAsset)
);

const expandable = computed(() => children.value.length > 0);

const jump = () => {
	if (isPruned.value || isCurrent.value) {
		return;
	}

	tree.setScope(branch);
	close();
};
</script>
<template>
	<div class="node">
		<div class="row" :class="{ current: isCurrent, pruned: isPruned }">
			<button
				v-if="expandable"
				type="button"
				class="twist"
				:aria-expanded="expanded"
				@click="tree.toggleExpanded(branch)"
			>
				<v-icon
					:icon="
						expanded
							? 'fa: fa-regular fa-chevron-down'
							: 'fa: fa-regular fa-chevron-right'
					"
					size="11"
				/>
			</button>
			<span v-else class="twist" />
			<button
				type="button"
				class="label text-body-2"
				:disabled="isPruned"
				@click="jump"
			>
				{{ node.visibleLabel }}
			</button>
		</div>
		<div v-if="expanded" class="children">
			<hierarchy-structure
				v-for="entry in children"
				:key="entry.branch.join('.')"
				:tree="tree"
				:node="entry.child"
				:branch="entry.branch"
				:close="close"
			/>
		</div>
	</div>
</template>
<style scoped lang="scss">
.children {
	margin-left: 0.75rem;
	border-left: 1px solid rgb(var(--color-secondary));
}

.row {
	display: flex;
	align-items: center;
	gap: 0.125rem;
	border-radius: 3px;

	&:hover {
		background: rgb(var(--color-secondary));
	}

	&.current {
		background: rgba(var(--v-theme-info), 0.12);
		box-shadow: inset 2px 0 0 rgb(var(--color-info));

		.label {
			font-weight: 600;
			cursor: default;
		}
	}

	&.pruned .label {
		text-decoration: line-through;
		opacity: 0.5;
		cursor: default;
	}
}

.twist {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 1.125rem;
	height: 1.5rem;
	color: rgb(var(--color-on-background));
}

.label {
	flex: 1 1 auto;
	min-width: 0;
	padding: 0.1875rem 0.25rem;
	overflow: hidden;
	text-align: left;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: rgb(var(--color-on-surface));
	cursor: pointer;
}
</style>
